import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useApp } from '../context/AppContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Search, Globe, FileText, Code, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminSEOPage = () => {
  const { getAuthHeaders } = useApp();
  const [settings, setSettings] = useState({
    site_title: '',
    meta_description: '',
    meta_keywords: '',
    og_title: '',
    og_description: '',
    og_image: '',
    robots_txt: '',
    head_scripts: '',
    canonical_url: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/settings/seo`);
      setSettings(data);
    } catch (error) {
      console.error('Failed to fetch SEO settings');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${BACKEND_URL}/api/settings/seo`, settings, {
        headers: getAuthHeaders(),
      });
      toast.success('SEO settings updated!');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex items-center gap-3 mb-8">
          <Search size={32} className="text-primary" />
          <div>
            <h1 className="text-3xl font-serif">SEO Settings</h1>
            <p className="text-muted-foreground">Optimizare pentru motoarele de căutare</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Page Title & Meta */}
          <div className="glass p-6">
            <div className="flex items-center gap-2 mb-4">
              <Globe size={20} className="text-primary" />
              <h2 className="text-xl font-semibold">Titlu & Meta Tags</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">
                  Titlu Site (apare în tab-ul browserului)
                </label>
                <input
                  type="text"
                  value={settings.site_title}
                  onChange={(e) => setSettings({ ...settings, site_title: e.target.value })}
                  placeholder="Rentiasi.ro - Apartamente de închiriat în Iași"
                  className="w-full bg-slate-900/50 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-primary"
                />
                <p className="text-xs text-gray-500 mt-1">Recomandat: 50-60 caractere | Actual: {settings.site_title?.length || 0}</p>
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">
                  Meta Description (descriere în rezultatele Google)
                </label>
                <textarea
                  value={settings.meta_description}
                  onChange={(e) => setSettings({ ...settings, meta_description: e.target.value })}
                  placeholder="Găsește apartamente de închiriat în Iași direct de la proprietar..."
                  rows={3}
                  className="w-full bg-slate-900/50 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-primary"
                />
                <p className="text-xs text-gray-500 mt-1">Recomandat: 150-160 caractere | Actual: {settings.meta_description?.length || 0}</p>
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">
                  Meta Keywords (separate prin virgulă)
                </label>
                <input
                  type="text"
                  value={settings.meta_keywords}
                  onChange={(e) => setSettings({ ...settings, meta_keywords: e.target.value })}
                  placeholder="apartamente iasi, inchirieri iasi, chirii iasi"
                  className="w-full bg-slate-900/50 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-primary"
                />
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">
                  Canonical URL (URL-ul principal al site-ului)
                </label>
                <input
                  type="url"
                  value={settings.canonical_url}
                  onChange={(e) => setSettings({ ...settings, canonical_url: e.target.value })}
                  placeholder="https://rentiasi.ro"
                  className="w-full bg-slate-900/50 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-primary"
                />
              </div>
            </div>
          </div>

          {/* Open Graph (Social Media) */}
          <div className="glass p-6">
            <div className="flex items-center gap-2 mb-4">
              <ExternalLink size={20} className="text-primary" />
              <h2 className="text-xl font-semibold">Open Graph (Social Media)</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Aceste setări controlează cum apare site-ul când este distribuit pe Facebook, LinkedIn, etc.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">OG Title</label>
                <input
                  type="text"
                  value={settings.og_title}
                  onChange={(e) => setSettings({ ...settings, og_title: e.target.value })}
                  placeholder="RentIași - Apartamente de Închiriat"
                  className="w-full bg-slate-900/50 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-primary"
                />
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">OG Description</label>
                <textarea
                  value={settings.og_description}
                  onChange={(e) => setSettings({ ...settings, og_description: e.target.value })}
                  placeholder="Cele mai bune apartamente de închiriat în Iași"
                  rows={2}
                  className="w-full bg-slate-900/50 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-primary"
                />
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">OG Image URL (imagine pentru social media)</label>
                <input
                  type="text"
                  value={settings.og_image}
                  onChange={(e) => setSettings({ ...settings, og_image: e.target.value })}
                  placeholder="https://rentiasi.ro/og-image.jpg"
                  className="w-full bg-slate-900/50 border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:border-primary"
                />
                <p className="text-xs text-gray-500 mt-1">Recomandat: 1200x630 px</p>
              </div>
            </div>
          </div>

          {/* Robots.txt */}
          <div className="glass p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FileText size={20} className="text-primary" />
                <h2 className="text-xl font-semibold">Robots.txt</h2>
              </div>
              <a 
                href={`${BACKEND_URL}/api/robots.txt`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline"
              >
                Vezi robots.txt →
              </a>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Controlează ce pagini pot fi indexate de motoarele de căutare.
            </p>
            
            <textarea
              value={settings.robots_txt}
              onChange={(e) => setSettings({ ...settings, robots_txt: e.target.value })}
              rows={8}
              className="w-full bg-slate-900/50 border border-white/10 rounded-lg px-4 py-3 font-mono text-sm focus:outline-none focus:border-primary"
              placeholder={`User-agent: *
Allow: /
Disallow: /admin/

Sitemap: https://rentiasi.ro/sitemap.xml`}
            />
          </div>

          {/* Sitemap */}
          <div className="glass p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Globe size={20} className="text-primary" />
                <h2 className="text-xl font-semibold">Sitemap XML</h2>
              </div>
              <a 
                href={`${BACKEND_URL}/api/sitemap.xml`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline"
              >
                Vezi sitemap.xml →
              </a>
            </div>
            <p className="text-sm text-muted-foreground">
              Sitemap-ul este generat automat și include toate paginile statice și apartamentele.
              <br />
              URL: <code className="bg-slate-800 px-2 py-1 rounded">{settings.canonical_url || 'https://rentiasi.ro'}/sitemap.xml</code>
            </p>
          </div>

          {/* Additional Scripts */}
          <div className="glass p-6">
            <div className="flex items-center gap-2 mb-4">
              <Code size={20} className="text-primary" />
              <h2 className="text-xl font-semibold">Scripturi Adiționale (Head)</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Adaugă cod pentru Google Analytics, Facebook Pixel, etc. Va fi inserat în {'<head>'}.
            </p>
            
            <textarea
              value={settings.head_scripts}
              onChange={(e) => setSettings({ ...settings, head_scripts: e.target.value })}
              rows={6}
              className="w-full bg-slate-900/50 border border-white/10 rounded-lg px-4 py-3 font-mono text-sm focus:outline-none focus:border-primary"
              placeholder={`<!-- Google Analytics -->
<script async src="https://www.googletagmanager.com/gtag/js?id=GA_TRACKING_ID"></script>`}
            />
          </div>

          {/* Save Button */}
          <Button type="submit" disabled={loading} className="w-full bg-primary text-white py-3">
            {loading ? 'Se salvează...' : 'Salvează Setările SEO'}
          </Button>
        </form>

        {/* Preview */}
        <div className="mt-8 glass p-6">
          <h2 className="text-xl font-semibold mb-4">📱 Preview Google</h2>
          <div className="bg-white text-black p-4 rounded-lg max-w-xl">
            <div className="text-blue-600 text-lg hover:underline cursor-pointer truncate">
              {settings.site_title || 'Titlu pagină'}
            </div>
            <div className="text-green-700 text-sm">
              {settings.canonical_url || 'https://rentiasi.ro'}
            </div>
            <div className="text-gray-600 text-sm mt-1 line-clamp-2">
              {settings.meta_description || 'Descrierea paginii va apărea aici...'}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSEOPage;
